import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BookingFormAPI from "../api/BookingFormAPI";
import TripsDetailsAPI from "../api/TripsDetailsAPI";
import toast from "react-hot-toast";
import BookingImg from "../assets/media/booking.jpg";

const BookingForm = () => {
  const navigate = useNavigate();

  const { slug } = useParams();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [bookingData, setBookingData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    emergencyContact: "",
    arrivingFrom: "",
    numberOfTravelers: "1",
    gender: "",
    destination: "",
    booking_for: "",
    mainTravelerTravelMode: "",
    batch_date: "",
  });

  const [travelers, setTravelers] = useState([]);

  const renderRequiredAsterisk = () => <span style={{ color: "red" }}>*</span>;

  const getEffectivePrice = (travelMode) => {
    if (!travelMode) return 0;
    if (
      travelMode.discount &&
      travelMode.discount !== null &&
      travelMode.discount !== "" &&
      travelMode.discount !== "0"
    ) {
      const discountValue = parseFloat(
        String(travelMode.discount).replace(/,/g, "")
      );
      if (!isNaN(discountValue) && discountValue > 0) {
        return discountValue;
      }
    }

    const typePrice = parseFloat(
      String(travelMode.type_price).replace(/,/g, "")
    );
    return typePrice;
  };

  // Helper function to check if discount is available for a travel mode
  const hasDiscount = (travelMode) => {
    if (!travelMode || !travelMode.discount) return false;

    // Check if discount exists and is not empty/null/undefined/0
    if (
      travelMode.discount !== null &&
      travelMode.discount !== "" &&
      travelMode.discount !== "0"
    ) {
      const discountValue = parseFloat(
        String(travelMode.discount).replace(/,/g, "")
      );
      return !isNaN(discountValue) && discountValue > 0;
    }

    return false;
  };

  // Initialize travelers array when number of travelers changes (excluding main traveler)
  useEffect(() => {
    const numTravelers = parseInt(bookingData.numberOfTravelers) || 1;
    // Only create additional traveler forms for travelers beyond the main one
    const additionalTravelers = numTravelers - 1;

    if (additionalTravelers > 0) {
      const newTravelers = Array(additionalTravelers)
        .fill(null)
        .map((_, index) => ({
          id: index + 2, // Start from 2 since main traveler is 1
          name: "",
          age: "",
          gender: "",
          travelMode: "",
        }));
      setTravelers(newTravelers);
    } else {
      setTravelers([]);
    }
  }, [bookingData.numberOfTravelers]);

  // Enhanced travel mode options
  const getEnhancedTravelModes = () => {
    if (!trip?.travel_mode) return [];
    return [...trip.travel_mode];
  };

  // Get travel modes for single traveler (main traveler)
  const getSingleTravelerModes = () => {
    const modes = getEnhancedTravelModes();
    // Filter modes that are suitable for single travelers
    return modes.filter(
      (mode) =>
        mode.trip_person.toLowerCase().includes("single") ||
        mode.trip_person.toLowerCase().includes("individual") ||
        !mode.trip_person.toLowerCase().includes("sharing")
    );
  };

  // Handle traveler data changes
  const handleTravelerChange = (travelerId, field, value) => {
    setTravelers((prev) =>
      prev.map((traveler) =>
        traveler.id === travelerId ? { ...traveler, [field]: value } : traveler
      )
    );

    // Clear traveler-specific errors
    const errorKey = `traveler_${travelerId}_${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({ ...prev, [errorKey]: "" }));
    }
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    const nameRegex = /^[A-Za-z ]{2,}$/;
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    const mobileRegex = /^[6-9]\d{9}$/;

    // Validate main booking data
    if (!bookingData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (!nameRegex.test(bookingData.firstName)) {
      newErrors.firstName =
        "Please enter a valid first name (minimum 2 characters, letters only)";
    }

    if (!bookingData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (!nameRegex.test(bookingData.lastName)) {
      newErrors.lastName =
        "Please enter a valid last name (minimum 2 characters, letters only)";
    }

    if (!bookingData.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(bookingData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!bookingData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!mobileRegex.test(bookingData.phone)) {
      newErrors.phone =
        "Please enter a valid 10-digit mobile number starting with 6-9";
    }

    if (!bookingData.emergencyContact) {
      newErrors.emergencyContact = "Emergency contact is required";
    } else if (!mobileRegex.test(bookingData.emergencyContact)) {
      newErrors.emergencyContact =
        "Please enter a valid 10-digit mobile number";
    }

    if (!bookingData.arrivingFrom) {
      newErrors.arrivingFrom = "Arriving from is required";
    }

    if (!bookingData.numberOfTravelers) {
      newErrors.numberOfTravelers = "Number of travelers is required";
    } else if (isNaN(Number(bookingData.numberOfTravelers))) {
      newErrors.numberOfTravelers = "Must be a valid number";
    }

    if (!bookingData.gender) {
      newErrors.gender = "Gender is required";
    }

    // Validate main traveler's travel mode
    if (!bookingData.mainTravelerTravelMode) {
      newErrors.mainTravelerTravelMode = "Room sharing selection is required";
    }

    if (!bookingData.batch_date) {
      newErrors.batch_date = "Please select a batch";
    }

    // Validate additional travelers (only if more than 1 traveler)
    if (parseInt(bookingData.numberOfTravelers) > 1) {
      travelers.forEach((traveler) => {
        if (!traveler.name.trim()) {
          newErrors[`traveler_${traveler.id}_name`] =
            "Traveler name is required";
        } else if (!nameRegex.test(traveler.name)) {
          newErrors[`traveler_${traveler.id}_name`] =
            "Please enter a valid name (minimum 2 characters, letters only)";
        }

        if (!traveler.age) {
          newErrors[`traveler_${traveler.id}_age`] = "Age is required";
        } else if (
          isNaN(traveler.age) ||
          traveler.age < 1 ||
          traveler.age > 120
        ) {
          newErrors[`traveler_${traveler.id}_age`] =
            "Please enter a valid age (1-120)";
        }

        if (!traveler.gender) {
          newErrors[`traveler_${traveler.id}_gender`] = "Gender is required";
        }

        if (!traveler.travelMode) {
          newErrors[`traveler_${traveler.id}_travelMode`] =
            "Travel mode is required";
        }
      });
    }

    return newErrors;
  };

  useEffect(() => {
    // const fetchTripDetails = async () => {
    //   try {
    //     const res = await TripsDetailsAPI.getTripBySlug({ slug });
    //     setTrip(res.data);

    //     setBookingData((prev) => ({
    //       ...prev,
    //       destination: res.data.Destination || "",
    //       booking_for: res.data.title || "",
    //     }));

    //     setLoading(false);
    //   } catch (err) {
    //     console.error("Error fetching trip:", err);
    //     setError("Failed to load trip details");
    //     setLoading(false);
    //   }
    // };
    const fetchTripDetails = async () => {
      try {
        const res = await TripsDetailsAPI.getTripBySlug({ slug });

        if (res.code === 200 && res.data) {
          setTrip(res.data);
          setBookingData((prev) => ({
            ...prev,
            destination: res.data.Destination || "",
            booking_for: res.data.title || "",
          }));
          setError(null);
        } else {
          setError(res.message || "Failed to load trip details");
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching trip:", err);
        setError("Failed to load trip details");
        setLoading(false);
      }
    };

    if (slug) fetchTripDetails();
  }, [slug]);

  const flattenedBatches =
    trip?.batches?.flatMap((month) => month.batches) || [];

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Input sanitization based on field type
    let processedValue = value;
    if (name === "firstName" || name === "lastName") {
      processedValue = value.replace(/[^A-Za-z\s]/g, "");
    } else if (name === "phone" || name === "emergencyContact") {
      processedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setBookingData((prev) => ({ ...prev, [name]: processedValue }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFieldBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    // Validate just this field
    const fieldErrors = validateForm();
    setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] }));
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = Object.keys(bookingData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      toast.error("Please correct the form errors.");
      return;
    }

    // try {
    // Create FormData for file upload
    const formData = new FormData();

    // Append all booking data
    Object.keys(bookingData).forEach((key) => {
      formData.append(key, bookingData[key]);
    });

    // Append travelers data for multiple travelers
    if (parseInt(bookingData.numberOfTravelers) > 1) {
      formData.append("travelers", JSON.stringify(travelers));
    }

    formData.append("slug", slug);

    const response = await BookingFormAPI.postBookingFormAPI(formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    if (response.code === 200) {
      const { razorpayOrder, amount, order_id } = response.data;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: amount,
        currency: "INR",
        netbanking: true,
        name: "Sorted Trip",
        image: `${process.env.PUBLIC_URL}/logo.png`,
        description: "Thanks for shopping with us",
        order_id: razorpayOrder,
        prefill: {
          name: `${bookingData.firstName} ${bookingData.lastName}`,
          email: bookingData.email,
          contact: bookingData.phone,
        },
        theme: {
          color: "#063e09",
        },
        notes: {
          data: bookingData.gender,
          address: bookingData.arrivingFrom + ", " + bookingData.destination,
        },
        handler: async function (paymentResponse) {
          try {
            const verifyRes = await BookingFormAPI.paymentConfirmed({
              razorpayPaymentId: paymentResponse.razorpay_payment_id,
              razorpayOrderId: paymentResponse.razorpay_order_id,
              razorpaySignature: paymentResponse.razorpay_signature,
              order_id,
            });

            if (verifyRes.code === 200) {
              toast.success("Booked Successfully");

              setBookingData({
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                emergencyContact: "",
                arrivingFrom: "",
                numberOfTravelers: "1",
                gender: "",
                destination: trip?.Destination || "",
                booking_for: trip?.title || "",
                mainTravelerTravelMode: "",
              });
              setTravelers([]);
              setErrors({});
              setTouched({});

              setTimeout(function () {
                navigate("/order-confirmation");
              }, 1500);
            } else {
              toast.error("Payment verification failed");
            }
          } catch (error) {
            toast.error("Payment verification error");
          }
        },
        modal: {
          ondismiss: async () => {
            toast.error("Payment Rejected");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    }
    // } catch (error) {
    //   toast.error("Something went wrong.");
    // }
  };

  // Calculate total price based on selected travel modes
  const calculateTotal = () => {
    if (!trip) return 0;

    const numberOfTravelers = Number(bookingData.numberOfTravelers) || 1;
    const enhancedModes = getEnhancedTravelModes();
    let total = 0;

    // Calculate for main traveler
    if (bookingData.mainTravelerTravelMode) {
      const mainTravelerMode = enhancedModes.find(
        (mode) => mode.trip_person === bookingData.mainTravelerTravelMode
      );
      if (mainTravelerMode) {
        const basePrice = getEffectivePrice(mainTravelerMode);
        const gstRate = parseFloat(mainTravelerMode.gst) / 100;
        const priceWithGST = basePrice + basePrice * gstRate;
        total += priceWithGST;
      }
    }

    // Calculate for additional travelers
    if (numberOfTravelers > 1 && travelers.length > 0) {
      travelers.forEach((traveler) => {
        if (traveler.travelMode) {
          const selectedMode = enhancedModes.find(
            (mode) => mode.trip_person === traveler.travelMode
          );
          if (selectedMode) {
            const basePrice = getEffectivePrice(selectedMode);
            const gstRate = parseFloat(selectedMode.gst) / 100;
            const priceWithGST = basePrice + basePrice * gstRate;
            total += priceWithGST;
          }
        }
      });
    }

    return Math.round(total);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN").format(price);
  };

  if (loading)
    return <div className="text-center py-5">Loading trip details...</div>;
  if (error) return <div className="text-center py-5 text-danger">{error}</div>;

  const numberOfTravelers = Number(bookingData.numberOfTravelers) || 1;
  const enhancedTravelModes = getEnhancedTravelModes();
  const singleTravelerModes = getSingleTravelerModes();


const formatBatchDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, "0");

  const month = date.toLocaleString("en-IN", {
    month: "short",
  });

  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

  return (
    <div className="container-fluid py-5" style={{ maxWidth: "1200px" }}>
      <div className="booking-header text-center mb-5">
        <h2 style={{ color: "#259dd1" }}>Book Your Trip</h2>
        <p className="text-muted">Complete your booking in just a few steps</p>
      </div>

      <div className="row g-4">
        {/* Left Column - Image and Trip Details */}
        <div className="col-lg-6">
          <div className="h-100 d-flex flex-column">
            <img
              src={BookingImg}
              alt="Booking Visual"
              className="img-fluid rounded shadow"
              style={{ maxHeight: "400px", objectFit: "cover" }}
            />
            {trip && (
              <div className="trip-highlights mt-4 p-3 bg-light rounded">
                <h5 className="mb-3">Trip Highlights</h5>
                <ul className="list-unstyled">
                  <li className="mb-2">
                    <i className="fas fa-map-marker-alt text-primary me-2"></i>
                    <strong>Destination:</strong> {trip.title}
                  </li>
                  <li className="mb-2">
                    <i className="fas fa-calendar-alt text-primary me-2"></i>
                    <strong>Duration:</strong> {trip.duration_day} Days /{" "}
                    {trip.duration_night} Nights
                  </li>
                  <li className="mb-2">
                    <i className="fas fa-calendar-alt text-primary me-2"></i>
                    <strong>Dine in:</strong> {trip.dine_in}
                  </li>
                  <li className="mb-2">
                    <i className="fas fa-calendar-alt text-primary me-2"></i>
                    <strong>Flight:</strong> {trip.flight}
                  </li>
                  <li className="mb-2">
                    <i className="fas fa-calendar-alt text-primary me-2"></i>
                    <strong>Hotel:</strong> {trip.hotel}
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Form and Invoice */}
        <div className="col-lg-6">
          {/* Invoice Summary */}
          {trip && (
            <div className="invoice-summary mb-4 p-4 bg-white rounded shadow-sm border">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Booking Summary</h5>
              </div>

              <div className="summary-details">
                <div className="row mb-2">
                  <div className="col-6 text-muted">Trip Package:</div>
                  <div className="col-6 text-end">{trip.title}</div>
                </div>

                <div className="row mb-2">
                  <div className="col-6 text-muted">Travelers:</div>
                  <div className="col-6 text-end">
                    {numberOfTravelers} person{numberOfTravelers > 1 ? "s" : ""}
                  </div>
                </div>

                {/* Main Traveler Details */}
                {bookingData.mainTravelerTravelMode &&
                  (() => {
                    const mainTravelerMode = enhancedTravelModes.find(
                      (mode) =>
                        mode.trip_person === bookingData.mainTravelerTravelMode
                    );
                    if (!mainTravelerMode) return null;

                    const effectivePrice = getEffectivePrice(mainTravelerMode);
                    const originalPrice = parseFloat(
                      mainTravelerMode.type_price.replace(/,/g, "")
                    );
                    const discountAvailable = hasDiscount(mainTravelerMode);
                    const gstAmount = Math.round(
                      (effectivePrice * parseFloat(mainTravelerMode.gst)) / 100
                    );

                    return (
                      <div key="main-traveler">
                        <div className="row mb-1">
                          <div className="col-6 text-muted">
                            {bookingData.firstName || "Main Traveler"} (
                            {mainTravelerMode.trip_person.replace("_", " ")}):
                          </div>
                          <div className="col-6 text-end">
                            {discountAvailable ? (
                              <>
                                <span
                                  style={{
                                    textDecoration: "line-through",
                                    color: "#6c757d",
                                  }}
                                >
                                  ₹{formatPrice(originalPrice)}
                                </span>
                                <br />
                                <span className="text-success">
                                  ₹{formatPrice(effectivePrice)}
                                </span>
                              </>
                            ) : (
                              <span>₹{formatPrice(effectivePrice)}</span>
                            )}
                          </div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-6 text-muted">
                            GST ({mainTravelerMode.gst}%):
                          </div>
                          <div className="col-6 text-end">
                            ₹{formatPrice(gstAmount)}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                {/* Additional Travelers Details */}
                {numberOfTravelers > 1 && travelers.length > 0 && (
                  <>
                    {travelers.map((traveler, index) => {
                      if (!traveler.travelMode) return null;

                      const selectedMode = enhancedTravelModes.find(
                        (mode) => mode.trip_person === traveler.travelMode
                      );
                      if (!selectedMode) return null;

                      const effectivePrice = getEffectivePrice(selectedMode);
                      const originalPrice = parseFloat(
                        selectedMode.type_price.replace(/,/g, "")
                      );
                      const discountAvailable = hasDiscount(selectedMode);
                      const gstAmount = Math.round(
                        (effectivePrice * parseFloat(selectedMode.gst)) / 100
                      );

                      return (
                        <div key={index}>
                          <div className="row mb-1">
                            <div className="col-6 text-muted">
                              {traveler.name || `Traveler ${index + 2}`} (
                              {selectedMode.trip_person.replace("_", " ")}):
                            </div>
                            <div className="col-6 text-end">
                              {discountAvailable ? (
                                <>
                                  <span
                                    style={{
                                      textDecoration: "line-through",
                                      color: "#6c757d",
                                    }}
                                  >
                                    ₹{formatPrice(originalPrice)}
                                  </span>
                                  <br />
                                  <span className="text-success">
                                    ₹{formatPrice(effectivePrice)}
                                  </span>
                                </>
                              ) : (
                                <span>₹{formatPrice(effectivePrice)}</span>
                              )}
                            </div>
                          </div>
                          <div className="row mb-2">
                            <div className="col-6 text-muted">
                              GST ({selectedMode.gst}%):
                            </div>
                            <div className="col-6 text-end">
                              ₹{formatPrice(gstAmount)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                <hr />
                <div className="row">
                  <div className="col-6">
                    <h6 className="mb-0">Total Amount:</h6>
                    <small className="text-muted">
                      (Inclusive of all taxes)
                    </small>
                  </div>
                  <div className="col-6 text-end">
                    <h5 className="mb-0" style={{ color: "#259dd1" }}>
                      ₹{formatPrice(calculateTotal())}
                    </h5>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Booking Form */}
          <form
            onSubmit={handleBookingSubmit}
            className="p-4 bg-white rounded shadow-sm border"
          >
            <h5 className="mb-4">Personal Details</h5>

            <div className="row g-3">
              <div className="col-md-6 mt-0">
                <div className="mb-3">
                  <label className="form-label">
                    First Name {renderRequiredAsterisk()}
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    className={`form-control ${touched.firstName && errors.firstName ? "is-invalid" : ""
                      }`}
                    value={bookingData.firstName}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    required
                  />
                  {touched.firstName && errors.firstName && (
                    <div className="invalid-feedback">{errors.firstName}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6 mt-0">
                <div className="mb-3">
                  <label className="form-label">
                    Last Name {renderRequiredAsterisk()}
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    className={`form-control ${touched.lastName && errors.lastName ? "is-invalid" : ""
                      }`}
                    value={bookingData.lastName}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    required
                  />
                  {touched.lastName && errors.lastName && (
                    <div className="invalid-feedback">{errors.lastName}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6 mt-0">
                <div className="mb-3">
                  <label className="form-label">
                    Email {renderRequiredAsterisk()}
                  </label>
                  <input
                    type="email"
                    name="email"
                    className={`form-control ${touched.email && errors.email ? "is-invalid" : ""
                      }`}
                    value={bookingData.email}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    required
                  />
                  {touched.email && errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6 mt-0">
                <div className="mb-3">
                  <label className="form-label">
                    Phone Number {renderRequiredAsterisk()}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    className={`form-control ${touched.phone && errors.phone ? "is-invalid" : ""
                      }`}
                    value={bookingData.phone}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    required
                  />
                  {touched.phone && errors.phone && (
                    <div className="invalid-feedback">{errors.phone}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6 mt-0">
                <div className="mb-3">
                  <label className="form-label">
                    Emergency Contact {renderRequiredAsterisk()}
                  </label>
                  <input
                    type="tel"
                    name="emergencyContact"
                    className={`form-control ${touched.emergencyContact && errors.emergencyContact
                      ? "is-invalid"
                      : ""
                      }`}
                    value={bookingData.emergencyContact}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    required
                  />
                  {touched.emergencyContact && errors.emergencyContact && (
                    <div className="invalid-feedback">
                      {errors.emergencyContact}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 mt-0">
                <div className="mb-3">
                  <label className="form-label">
                    Arriving From {renderRequiredAsterisk()}
                  </label>
                  <input
                    type="text"
                    name="arrivingFrom"
                    className={`form-control ${touched.arrivingFrom && errors.arrivingFrom
                      ? "is-invalid"
                      : ""
                      }`}
                    value={bookingData.arrivingFrom}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    required
                  />
                  {touched.arrivingFrom && errors.arrivingFrom && (
                    <div className="invalid-feedback">
                      {errors.arrivingFrom}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 mt-0">
                <div className="mb-3">
                  <label className="form-label">
                    Number of Travelers {renderRequiredAsterisk()}
                  </label>
                  <input
                    type="number"
                    name="numberOfTravelers"
                    className={`form-control ${touched.numberOfTravelers && errors.numberOfTravelers
                      ? "is-invalid"
                      : ""
                      }`}
                    value={bookingData.numberOfTravelers}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    min="1"
                    required
                  />
                  {touched.numberOfTravelers && errors.numberOfTravelers && (
                    <div className="invalid-feedback">
                      {errors.numberOfTravelers}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 mt-0">
                <div className="mb-3">
                  <label className="form-label">
                    Gender {renderRequiredAsterisk()}
                  </label>
                  <select
                    name="gender"
                    className={`form-control ${touched.gender && errors.gender ? "is-invalid" : ""
                      }`}
                    value={bookingData.gender}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  {touched.gender && errors.gender && (
                    <div className="invalid-feedback">{errors.gender}</div>
                  )}
                </div>
              </div>


              <div className="col-md-12 mt-0">
                <div className="mb-3">
                  <label className="form-label">
                    Select Batch <span style={{ color: "red" }}>*</span>
                  </label>

                  <select
                    name="batch_date"
                    className={`form-select ${errors.batch_date ? "is-invalid" : ""}`}
                    value={bookingData.batch_date}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                  >
                    <option value="">-- Select Batch --</option>

                    {flattenedBatches.map((batch) => (
                      <option key={batch.id} value={batch.id}>
                        {formatBatchDate(batch.batche_from)} → {formatBatchDate(batch.batche_To)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Main Traveler's Room Sharing */}
              <div className="col-md-12 mt-0">
                <div className="mb-3">
                  <label className="form-label">
                    Room Sharing {renderRequiredAsterisk()}
                  </label>
                  <select
                    className={`form-select ${errors.mainTravelerTravelMode ? "is-invalid" : ""
                      }`}
                    name="mainTravelerTravelMode"
                    value={bookingData.mainTravelerTravelMode}
                    onChange={handleChange}
                    onBlur={handleFieldBlur}
                  >
                    <option value="">-- Select Room Sharing --</option>
                    {enhancedTravelModes.map((mode, modeIndex) => {
                      const effectivePrice = getEffectivePrice(mode);
                      const originalPrice = parseFloat(
                        mode.type_price.replace(/,/g, "")
                      );
                      const discountAvailable = hasDiscount(mode);

                      return (
                        <option key={modeIndex} value={mode.trip_person}>
                          {mode.trip_person.replace("_", " ").toUpperCase()} -{" "}
                          {discountAvailable
                            ? `₹${formatPrice(effectivePrice)}`
                            : `₹${formatPrice(effectivePrice)}`}{" "}
                          + {mode.gst}% GST
                        </option>
                      );
                    })}
                  </select>
                  {errors.mainTravelerTravelMode && (
                    <div className="invalid-feedback d-block">
                      {errors.mainTravelerTravelMode}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Travelers Section - Only show if more than 1 traveler */}
            {numberOfTravelers > 1 && travelers.length > 0 && (
              <div className="travelers-section mt-4 pt-4 border-top">
                <h5 className="mb-4">Additional Travelers Details</h5>

                {travelers.map((traveler, index) => (
                  <div
                    key={traveler.id}
                    className="traveler-card mb-4 p-3 border rounded bg-light"
                  >
                    <h6 className="mb-3" style={{ color: "#259dd1" }}>
                      Traveler {index + 2} Details
                    </h6>

                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Full Name {renderRequiredAsterisk()}
                          </label>
                          <input
                            type="text"
                            className={`form-control ${errors[`traveler_${traveler.id}_name`]
                              ? "is-invalid"
                              : ""
                              }`}
                            onChange={(e) =>
                              handleTravelerChange(
                                traveler.id,
                                "name",
                                e.target.value.replace(/[^A-Za-z\s]/g, "")
                              )
                            }
                            placeholder="Enter full name"
                            required
                          />
                          {errors[`traveler_${traveler.id}_name`] && (
                            <div className="invalid-feedback">
                              {errors[`traveler_${traveler.id}_name`]}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Age {renderRequiredAsterisk()}
                          </label>
                          <input
                            type="number"
                            className={`form-control ${errors[`traveler_${traveler.id}_age`]
                              ? "is-invalid"
                              : ""
                              }`}
                            value={traveler.age}
                            onChange={(e) =>
                              handleTravelerChange(
                                traveler.id,
                                "age",
                                e.target.value
                              )
                            }
                            placeholder="Enter age"
                            min="1"
                            max="120"
                            required
                          />
                          {errors[`traveler_${traveler.id}_age`] && (
                            <div className="invalid-feedback">
                              {errors[`traveler_${traveler.id}_age`]}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Gender {renderRequiredAsterisk()}
                          </label>
                          <select
                            className={`form-control ${errors[`traveler_${traveler.id}_gender`]
                              ? "is-invalid"
                              : ""
                              }`}
                            value={traveler.gender}
                            onChange={(e) =>
                              handleTravelerChange(
                                traveler.id,
                                "gender",
                                e.target.value
                              )
                            }
                            required
                          >
                            <option value="">Select Gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                          </select>
                          {errors[`traveler_${traveler.id}_gender`] && (
                            <div className="invalid-feedback">
                              {errors[`traveler_${traveler.id}_gender`]}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Room Sharing {renderRequiredAsterisk()}
                          </label>
                          <select
                            className={`form-select ${errors[`traveler_${traveler.id}_travelMode`]
                              ? "is-invalid"
                              : ""
                              }`}
                            name={`travelMode_${traveler.id}`}
                            value={traveler.travelMode || ""}
                            onChange={(e) =>
                              handleTravelerChange(
                                traveler.id,
                                "travelMode",
                                e.target.value
                              )
                            }
                          >
                            <option value="">-- Room Sharing --</option>
                            {enhancedTravelModes.map((mode, modeIndex) => {
                              const effectivePrice = getEffectivePrice(mode);
                              const originalPrice = parseFloat(
                                mode.type_price.replace(/,/g, "")
                              );
                              const discountAvailable = hasDiscount(mode);

                              return (
                                <option
                                  key={modeIndex}
                                  value={mode.trip_person}
                                >
                                  {mode.trip_person
                                    .replace("_", " ")
                                    .toUpperCase()}{" "}
                                  -{" "}
                                  {discountAvailable
                                    ? `₹${formatPrice(effectivePrice)}`
                                    : `₹${formatPrice(effectivePrice)}`}{" "}
                                  + {mode.gst}% GST
                                </option>
                              );
                            })}
                          </select>
                          {errors[`traveler_${traveler.id}_travelMode`] && (
                            <div className="invalid-feedback d-block">
                              {errors[`traveler_${traveler.id}_travelMode`]}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 pt-3 border-top">
              <button
                type="submit"
                className="btn btn-primary w-100 py-2"
                style={{ backgroundColor: "#259dd1", borderColor: "#259dd1" }}
              >
                <i className="fas fa-lock me-2"></i>
                Proceed to Secure Payment
              </button>
              <p className="text-muted text-center mt-3 mb-0">
                <small>Your information is secure and will not be shared</small>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;
